import React, { useState, useEffect } from 'react';

const Pricing: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('annual');
  const [highlightedPlan, setHighlightedPlan] = useState<string | null>(null);

  // Handle hash highlighting
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      
      if (hash) {
        setHighlightedPlan(hash);
        // Remove highlight after 3 seconds
        setTimeout(() => {
          setHighlightedPlan(null);
        }, 3000);
      }
    };

    // Check for hash on component mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const plans = [
    {
      name: "Pay as You Go",
      description: "Flexible pricing model - pay only for what you use",
      price: {
        monthly: "$0.05",
        annual: "$0.05"
      },
      priceNote: "per verification",
      features: [
        "ML-based identity validation",
        "Behavioral analytics",
        "Social media profiling",
        "Device takeover detection",
        "Network intelligence",
        "Background checks",
        "API access",
        "Real-time processing"
      ],
      limitations: [
        "No minimum commitment",
        "Standard support"
      ],
      cta: "Start Free Trial",
      popular: false
    },
    {
      name: "Volume Plans",
      description: "Better rates for higher volume usage",
      price: {
        monthly: "Custom",
        annual: "Custom"
      },
      priceNote: "volume discounts available",
      features: [
        "Everything in Pay as You Go",
        "Priority support",
        "Custom risk thresholds",
        "Advanced analytics",
        "Webhook notifications",
        "SLA guarantee",
        "Integration assistance",
        "Dedicated support"
      ],
      limitations: [
        "Minimum volume requirements",
        "Annual commitment"
      ],
      cta: "Contact Sales",
      popular: true
    },
    {
      name: "Enterprise",
      description: "Custom solutions for large organizations",
      price: {
        monthly: "Custom",
        annual: "Custom"
      },
      priceNote: "tailored pricing",
      features: [
        "All fraud detection capabilities",
        "Custom AI model training",
        "24/7 dedicated support",
        "White-label solutions",
        "Custom integrations",
        "Compliance reporting",
        "On-premise deployment",
        "Dedicated account manager"
      ],
      limitations: [],
      cta: "Contact Sales",
      popular: false
    }
  ];

  const features = [
    {
      category: "Identity Validation",
      items: [
        "ML-based email validation",
        "Social media profiling",
        "Phone number verification",
        "Cross-platform identity matching"
      ]
    },
    {
      category: "Behavioral Analytics",
      items: [
        "Device behavior monitoring",
        "Cognitive attribute tracking",
        "Usage pattern analysis",
        "Anomaly detection"
      ]
    },
    {
      category: "Security Features",
      items: [
        "Device takeover detection",
        "Bot & emulator detection",
        "VPN & proxy detection",
        "Mock setting identification"
      ]
    },
    {
      category: "Background Checks",
      items: [
        "Criminal record checks",
        "Court case lookups",
        "FIR verification",
        "Legal background screening"
      ]
    },
    {
      category: "Network Intelligence",
      items: [
        "Network signal analysis",
        "Geolocation verification",
        "Anonymization detection",
        "Network risk scoring"
      ]
    },
    {
      category: "API & Integration",
      items: [
        "REST API access",
        "Real-time processing",
        "Webhook notifications",
        "Developer documentation"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="pt-[5rem] sm:pt-[7rem] lg:pt-[11rem] pb-[2rem] sm:pb-[2.5rem] bg-black">
        <div className="container mx-auto px-[1.5rem] sm:px-[2rem]">
          <div className="w-full text-center">
            <h1 className="text-[1.5rem] sm:text-[1.75rem] lg:text-[2rem] xl:text-[3rem] 2xl:text-[3.75rem] font-black text-white mb-4 xl:mb-6 leading-tight break-words">
              Simple, <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">Transparent</span> Pricing
            </h1>
            <p className="text-[0.75rem] sm:text-[0.8125rem] lg:text-[0.875rem] xl:text-[1rem] text-white leading-relaxed mb-6 xl:mb-8">
              Choose the plan that fits your business needs. No hidden fees, no surprises.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-0">
              <span className={`text-sm sm:text-base lg:text-[1.125rem] ${billingCycle === 'monthly' ? 'text-white font-semibold' : 'text-white/60'}`}>
                Monthly
              </span>
              <button
                onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'annual' : 'monthly')}
                className="relative inline-flex h-5 w-10 sm:h-6 sm:w-11 items-center rounded-full bg-[#26ab4d] transition-colors focus:outline-none focus:ring-2 focus:ring-[#26ab4d] focus:ring-offset-2 focus:ring-offset-black"
              >
                <span
                  className={`inline-block h-3.5 w-3.5 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                    billingCycle === 'annual' ? 'translate-x-5 sm:translate-x-6' : 'translate-x-0.5 sm:translate-x-1'
                  }`}
                />
              </button>
              <span className={`text-sm sm:text-base lg:text-[1.125rem] ${billingCycle === 'annual' ? 'text-white font-semibold' : 'text-white/60'}`}>
                Annual
              </span>
              {billingCycle === 'annual' && (
                <span className="bg-gradient-to-r from-[#26ab4d]/30 to-[#1e8a3f]/30 text-[#26ab4d] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-semibold border border-[#26ab4d]/50">
                  Save 20%
                </span>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pt-[2rem] sm:pt-[2.5rem] pb-[3rem] sm:pb-[4rem]">
        <div className="container mx-auto px-[1.5rem] sm:px-[2rem]">
          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {plans.map((plan, index) => {
                // Generate section ID for each plan
                const sectionId = plan.name
                  .toLowerCase()
                  .replace(/[^a-z0-9\s-]/g, '') // Keep hyphens
                  .replace(/\s+/g, '-')
                  .trim();
                
                return (
                  <div
                    key={index}
                    id={sectionId}
                    className={`bg-gradient-to-br from-[#26ab4d]/10 via-black to-[#1e8a3f]/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 border scroll-mt-20 ${
                      plan.popular ? 'border-[#26ab4d] relative ring-2 ring-[#26ab4d]/30' : 'border-[#26ab4d]/30'
                    } hover:border-[#26ab4d]/70 hover:ring-[#26ab4d]/40 transition-all duration-300 ${
                      highlightedPlan === sectionId
                        ? 'border-[#26ab4d] ring-4 ring-[#26ab4d]/50 shadow-2xl shadow-[#26ab4d]/30'
                        : ''
                    }`}
                  >
                  {plan.popular && (
                    <div className="absolute -top-3 sm:-top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] text-white px-3 sm:px-4 py-1 sm:py-2 rounded-full text-xs sm:text-sm font-bold shadow-lg shadow-[#26ab4d]/30">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6 sm:mb-8">
                    <h3 className="text-[1.25rem] sm:text-[1.5rem] lg:text-[1.75rem] xl:text-[2rem] font-bold text-white mb-2 sm:mb-3">{plan.name}</h3>
                    <p className="text-white mb-4 sm:mb-6 text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem]">{plan.description}</p>
                    
                    <div className="mb-6 sm:mb-8">
                      <div className="text-[1.75rem] sm:text-[2.25rem] lg:text-[2.5rem] xl:text-[3rem] font-black text-white mb-1.5 sm:mb-2">{plan.price[billingCycle]}</div>
                      {plan.priceNote && (
                        <div className="text-white/70 text-xs sm:text-sm">{plan.priceNote}</div>
                      )}
                    </div>

                    <button
                      className={`w-full py-2 sm:py-2.5 lg:py-3 xl:py-4 px-4 sm:px-6 lg:px-8 rounded-full text-sm sm:text-base lg:text-lg font-bold tracking-wide transition-all duration-300 ${
                        plan.popular
                          ? 'bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f] text-white hover:from-[#1e8a3f] hover:to-[#26ab4d] shadow-lg shadow-[#26ab4d]/30'
                          : 'bg-gradient-to-r from-[#26ab4d]/80 to-[#1e8a3f]/80 text-white hover:from-[#26ab4d] hover:to-[#1e8a3f] border border-[#26ab4d]/50'
                      }`}
                    >
                      {plan.cta}
                    </button>
                  </div>

                  <div className="space-y-4 sm:space-y-6">
                    <h4 className="font-bold text-[#26ab4d] text-xs sm:text-sm uppercase tracking-wide">What's included:</h4>
                    <ul className="space-y-2 sm:space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start text-white text-[0.75rem] sm:text-xs lg:text-sm">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[#26ab4d] mr-2 sm:mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    {plan.limitations.length > 0 && (
                      <div className="pt-4 sm:pt-6 border-t border-[#26ab4d]/30">
                        <h4 className="font-bold text-white/80 text-xs sm:text-sm uppercase tracking-wide mb-2 sm:mb-3">Limitations:</h4>
                        <ul className="space-y-1.5 sm:space-y-2">
                          {plan.limitations.map((limitation, limitationIndex) => (
                            <li key={limitationIndex} className="flex items-start text-white/70 text-[0.75rem] sm:text-xs lg:text-sm">
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white/50 mr-2 sm:mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                              {limitation}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Feature Comparison */}
      <section className="py-[3rem] sm:py-[4rem] bg-gradient-to-br from-[#26ab4d]/20 via-black to-[#1e8a3f]/20">
        <div className="container mx-auto px-[1.5rem] sm:px-[2rem]">
          <div className="w-full">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12">
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] xl:text-[3rem] font-black text-white mb-3 sm:mb-4">
                <span className="text-white">Feature </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">Comparison</span>
              </h2>
              <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem] xl:text-[1.125rem] text-white">See what's included in each plan</p>
            </div>

            <div className="overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6">
              <table className="w-full border-collapse min-w-[640px]">
                <thead>
                  <tr className="border-b border-[#26ab4d]/30">
                    <th className="text-left py-3 sm:py-4 px-2 sm:px-3 lg:px-6 font-bold text-white text-xs sm:text-sm lg:text-[1rem]">Features</th>
                    <th className="text-center py-3 sm:py-4 px-2 sm:px-3 lg:px-6 font-bold text-white text-xs sm:text-sm lg:text-[1rem]">Pay as You Go</th>
                    <th className="text-center py-3 sm:py-4 px-2 sm:px-3 lg:px-6 font-bold text-white text-xs sm:text-sm lg:text-[1rem]">Volume Plans</th>
                    <th className="text-center py-3 sm:py-4 px-2 sm:px-3 lg:px-6 font-bold text-white text-xs sm:text-sm lg:text-[1rem]">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {features.map((feature, index) => (
                    <React.Fragment key={index}>
                      <tr className="border-b border-[#26ab4d]/20">
                        <td colSpan={4} className="py-2.5 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6 font-bold text-[#26ab4d] bg-gradient-to-r from-[#26ab4d]/20 via-[#26ab4d]/10 to-transparent text-xs sm:text-sm lg:text-[1rem]">
                          {feature.category}
                        </td>
                      </tr>
                      {feature.items.map((item, itemIndex) => (
                        <tr key={itemIndex} className="border-b border-[#26ab4d]/10 hover:bg-[#26ab4d]/5 transition-colors">
                          <td className="py-2.5 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6 text-white text-[0.75rem] sm:text-xs lg:text-[0.875rem]">{item}</td>
                          <td className="text-center py-2.5 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-[#26ab4d] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </td>
                          <td className="text-center py-2.5 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-[#26ab4d] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </td>
                          <td className="text-center py-2.5 sm:py-3 lg:py-4 px-2 sm:px-3 lg:px-6">
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-[#26ab4d] mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-[3rem] sm:py-[4rem] bg-black">
        <div className="container mx-auto px-[1.5rem]">
          <div className="w-full">
            <div className="text-center mb-8 sm:mb-10 lg:mb-12">
              <h2 className="text-[1.5rem] sm:text-[2rem] lg:text-[2.5rem] xl:text-[3rem] font-black text-white mb-3 sm:mb-4">
                <span className="text-white">Frequently Asked </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#26ab4d] to-[#1e8a3f]">Questions</span>
              </h2>
              <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem] xl:text-[1.125rem] text-white">Everything you need to know about our pricing</p>
            </div>

            <div className="space-y-4 sm:space-y-6">
              <div className="bg-gradient-to-br from-[#26ab4d]/10 via-black to-[#1e8a3f]/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 lg:p-8 border border-[#26ab4d]/30 hover:border-[#26ab4d]/50 transition-all duration-300">
                <h3 className="text-sm sm:text-base lg:text-[1.125rem] font-bold text-white mb-2 sm:mb-3">How does the verification limit work?</h3>
                <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem] text-white leading-relaxed">
                  Each plan includes a monthly verification limit. Each API call to verify an identity counts as one verification. 
                  If you exceed your limit, you'll be charged for additional verifications at a per-verification rate.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#26ab4d]/10 via-black to-[#1e8a3f]/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 lg:p-8 border border-[#26ab4d]/30 hover:border-[#26ab4d]/50 transition-all duration-300">
                <h3 className="text-sm sm:text-base lg:text-[1.125rem] font-bold text-white mb-2 sm:mb-3">Can I change plans anytime?</h3>
                <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem] text-white leading-relaxed">
                  Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately, 
                  and we'll prorate any billing differences.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#26ab4d]/10 via-black to-[#1e8a3f]/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 lg:p-8 border border-[#26ab4d]/30 hover:border-[#26ab4d]/50 transition-all duration-300">
                <h3 className="text-sm sm:text-base lg:text-[1.125rem] font-bold text-white mb-2 sm:mb-3">Is there a free trial?</h3>
                <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem] text-white leading-relaxed">
                  Yes, we offer a 14-day free trial for all paid plans. No credit card required to start. 
                  You get full access to all features during your trial period.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#26ab4d]/10 via-black to-[#1e8a3f]/10 backdrop-blur-sm rounded-xl p-4 sm:p-6 lg:p-8 border border-[#26ab4d]/30 hover:border-[#26ab4d]/50 transition-all duration-300">
                <h3 className="text-sm sm:text-base lg:text-[1.125rem] font-bold text-white mb-2 sm:mb-3">What's included in Enterprise support?</h3>
                <p className="text-[0.8125rem] sm:text-[0.875rem] lg:text-[1rem] text-white leading-relaxed">
                  Enterprise customers get 24/7 dedicated support, a dedicated account manager, 
                  custom integrations, white-label solutions, and priority feature requests.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Pricing;
